/**
 * WebSocket Service for Real-Time Clock Synchronization
 *
 * This service manages WebSocket connections to sync servers and implements
 * a robust clock synchronization algorithm using offset sampling. The service:
 *
 * 1. Establishes and maintains WebSocket connection with auto-reconnect
 * 2. Performs clock synchronization by sending multiple requests and calculating median offset
 * 3. Handles different message types from the sync server
 * 4. Provides epoch time adjusted for server clock offset
 *
 * Clock synchronization works by:
 * - Sending periodic "sync" requests to the server
 * - Server responds with its timestamp
 * - Calculate offset = server_time - round_trip_time/2 - client_time
 * - Collect multiple samples and use median value for stability
 * - Only declare "ready" once samples are consistent (settled)
 */

import { WEBSOCKET_CONFIG } from '../../constants'

import type { ChangeObject, StateObject, SystemObject } from 'types/sync'
/**
 * Collects clock offset samples and calculates median value
 *
 * Why use median over average?
 * - Outliers from network jitter would skew average
 * - Median gives us a robust estimate that ignores extreme values
 */
class OffsetSampler {
  #samples: Array<number>

  constructor() {
    this.#samples = []
  }

  /**
   * Get the number of samples collected
   */
  getSize = () => {
    return this.#samples.length
  }

  /**
   * Calculate the median of collected samples
   * Returns the new responseOffset if no samples exist yet (first iteration)
   *
   * @param responseOffset - The newly received offset to consider
   * @returns The median of all samples (or responseOffset if no samples)
   */
  median = (responseOffset: number) => {
    if (this.#samples.length === 0) {
      return responseOffset
    }
    // Sort samples to find median
    const sorted = this.#samples.slice().sort()
    // Even number of samples: return average of two middle values
    if (sorted.length % 2 === 0) {
      const left = sorted[sorted.length / 2 - 1]
      const right = sorted[sorted.length / 2]
      return (left + right) / 2
    } else {
      // Odd number: return middle value
      return sorted[Math.floor(sorted.length / 2)]
    }
  }

  /**
   * Add a new offset sample to the collection
   *
   * @param sample - The clock offset sample (in milliseconds)
   */
  add = (sample: number) => {
    this.#samples[this.#samples.length] = sample
  }

  /**
   * Clear all collected samples (start a new sync cycle)
   */
  reset = () => {
    this.#samples = []
  }
}

/**
 * Base message class for WebSocket communication
 */
class Message {
  type: 'Unknown' | 'sync'

  constructor() {
    this.type = 'Unknown'
  }
}

/**
 * Message sent by client to request clock synchronization
 *
 * Client records its current time to calculate round-trip time
 * when server responds.
 */
class SyncRequestMessage extends Message {
  client_time: number

  constructor() {
    super()
    this.type = 'sync'
    this.client_time = Date.now() // Record when we sent the request
  }
}

/**
 * Message sent by server in response to clock sync request
 *
 * Contains both client's timestamp and server's timestamp,
 * allowing calculation of clock offset.
 */
class SyncResponseMessage extends Message {
  client_time: number // When client sent the request
  server_time: number // When server sent the response

  constructor() {
    super()
    this.type = 'sync'
    this.client_time = Date.now()
    this.server_time = Date.now()
  }
}

/**
 * WebSocket Service for real-time synchronization
 *
 * Manages WebSocket connection, clock synchronization, and message routing.
 * Automatically handles reconnection with exponential backoff.
 */
export default class WebsocketService {
  /**
   * Factory method to create a new WebSocketService instance
   *
   * @param connectionString - WebSocket URL to connect to
   * @param readyHandler - Called when clock sync is complete
   * @param stateHandler - Called for 'state' messages from server
   * @param changeHandler - Called for 'change' messages from server
   * @param systemHandler - Called for 'system' messages from server
   * @returns New WebSocketService instance
   */
  public static instantiate(
    connectionString: string,
    readyHandler: () => void,
    stateHandler: (data: StateObject) => void,
    changeHandler: (data: ChangeObject) => void,
    systemHandler: (data: SystemObject) => void,
  ) {
    return new WebsocketService(
      connectionString,
      readyHandler,
      stateHandler,
      changeHandler,
      systemHandler,
      new OffsetSampler(),
    )
  }

  private client: WebSocket | null = null // WebSocket connection instance
  private connecting = false // Are we currently connecting?
  private reconnectTimeout = 5000 // Current reconnect delay (ms), doubles on each failure
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null // Timer for reconnection
  private clockSynced = false // Has clock synchronization completed?
  private clockOffset = 0 // Offset between client and server clock (ms)
  private updatingClock = false // Currently collecting sync samples?

  constructor(
    private connectionString: string,
    private readyHandler: () => void,
    private stateHandler: (data: StateObject) => void,
    private changeHandler: (data: ChangeObject) => void,
    private systemHandler: (data: SystemObject) => void,
    private offsetSampler: OffsetSampler,
  ) {}

  /**
   * Send a clock synchronization request to the server
   */
  private sendClockPing = () => {
    const request = new SyncRequestMessage()
    this.send(JSON.stringify(request))
  }

  /**
   * Start the clock synchronization process
   *
   * Sends periodic sync requests to the server at configured intervals.
   * These samples are collected and analyzed to determine clock offset.
   */
  private updateClock = () => {
    this.updatingClock = true
    this.offsetSampler.reset()

    // Send sync requests at regular intervals
    const updateTimer = window.setInterval(() => {
      if (!this.updatingClock) {
        clearInterval(updateTimer)
      } else {
        this.sendClockPing()
      }
    }, WEBSOCKET_CONFIG.DELAY_BETWEEN_CLOCK_REQUESTS_MS)
  }

  /**
   * Calculate the clock offset between client and server
   *
   * Network latency calculation:
   * - roundTripTime = total time from request sent to response received
   * - Assume network latency is symmetric: latency ≈ roundTripTime / 2
   *
   * Clock offset formula:
   * offset = serverTime - latency - clientTime
   *
   * This tells us how much we need to adjust our clock to match server time.
   *
   * @param response - Server's response containing timestamps
   * @returns Clock offset in milliseconds, or UNACCEPTABLE_RESPONSE if RTT is too high
   */
  private calculateServerTimeOffset(response: SyncResponseMessage) {
    const now = Date.now()
    const roundTripTime = now - response.client_time

    // Validate round trip time (must be positive and within acceptable range)
    // Too high RTT suggests network issues or server overload
    if (
      !(
        roundTripTime >= 0 &&
        roundTripTime <= WEBSOCKET_CONFIG.ACCEPTABLE_ROUNDTRIP_MS
      )
    ) {
      console.log('*** AP Sync Client: dropping response, unacceptable RTT')
      return WEBSOCKET_CONFIG.UNACCEPTABLE_RESPONSE
    }

    // Calculate offset: server time minus half the round trip time (assumed latency)
    // minus when the client sent the request
    const offset =
      response.server_time - roundTripTime / 2 - response.client_time

    // Debug logs (commented out):
    // console.log(`calculating offset, clientTime: ${response.client_time}, serverTime: ${response.server_time}, now: ${now}`);
    // console.log(`round trip time: ${roundTripTime}, offset: ${offset}`);

    return offset
  }

  /**
   * Handle clock synchronization response from server
   *
   * This processes sync responses and determines when clock is "ready":
   * 1. Calculate offset from response
  2. Add sample to collection
  3. Check if samples have "settled" (consistent results)
  4. Declare ready when settled OR max samples reached
  5. Schedule periodic resyncs
   *
   * @param parsedObject - Server response message
   */
  private syncHandler(parsedObject: ChangeObject) {
    // Parse server response as SyncResponseMessage
    const response = Object.assign(new SyncResponseMessage(), parsedObject)
    const responseOffset = this.calculateServerTimeOffset(response)

    // Skip if round trip time was too high (unreliable)
    if (responseOffset === WEBSOCKET_CONFIG.UNACCEPTABLE_RESPONSE) {
      return
    }

    // Add this sample to our collection
    this.offsetSampler.add(responseOffset)

    // Calculate median of all samples (more robust than average)
    const medianOffset = this.offsetSampler.median(responseOffset)

    // Debug log (commented out):
    // console.log(`responseOffset ${responseOffset} - medianOffset ${medianOffset} = ${responseOffset - medianOffset}`);

    // Determine if we have enough samples and they're "settled" (consistent)
    const hasEnoughSamples =
      this.offsetSampler.getSize() >= WEBSOCKET_CONFIG.MIN_SAMPLES_NUMBER
    const isSettled =
      Math.abs(responseOffset - medianOffset) <=
      WEBSOCKET_CONFIG.SETTLING_THRESHOLD
    const reachedMaxSamples =
      this.offsetSampler.getSize() >= WEBSOCKET_CONFIG.MAX_SAMPLES_NUMBER

    // Ready if: (enough samples AND settled) OR (reached max samples)
    if ((hasEnoughSamples && isSettled) || reachedMaxSamples) {
      const hasSynced = this.clockSynced
      this.clockOffset = medianOffset

      // Call ready handler only on first sync
      if (!hasSynced) {
        this.clockSynced = true
        this.readyHandler()
        console.info('Clock Sync: minimum response threshold achieved, ready')
      }

      // If we collected max samples, stop this sync cycle and schedule next one
      if (reachedMaxSamples) {
        this.updatingClock = false

        // Schedule periodic resyncs (clocks drift over time)
        setTimeout(() => {
          this.updateClock()
        }, WEBSOCKET_CONFIG.RESYNC_DELAY_MS)

        console.info(
          `Clock Sync: clock synced using ${
            WEBSOCKET_CONFIG.MAX_SAMPLES_NUMBER
          } samples, rescheduling in ${
            WEBSOCKET_CONFIG.RESYNC_DELAY_MS / 1000
          } seconds`,
        )
      }
    }
  }

  /**
   * Reconnect to the server with exponential backoff
   *
   * Reconnection strategy:
   * - Doubles the delay each time (5s → 10s → 20s → 40s...)
   * - Prevents hammering server during outages
   * - Clears existing connection before reconnecting
   */
  public reconnect() {
    if (this.connecting) {
      console.log('Already connecting')
    }
    this.connecting = true
    console.log('*** AP Sync Client: Reconnecting...')
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }

    // Exponential backoff: double the delay
    this.reconnectTimeout *= 2

    // Close existing connection if any
    if (this.client != null) {
      this.client.close()
    }
    this.client = null

    // Schedule reconnection
    this.reconnectTimer = setTimeout(
      () => this.connect(),
      this.reconnectTimeout,
    )
    console.log(
      `*** AP Sync Client: Reconnecting in ${
        this.reconnectTimeout / 1000
      } seconds`,
    )
  }

  /**
   * Called when WebSocket connection opens successfully
   *
   * Resets connection state and starts clock synchronization.
   */
  private socketOpenHandler = () => {
    console.log('*** AP Sync Client: Client is now connected')
    this.connecting = false
    // Reset reconnect timeout to initial value (successful connection)
    this.reconnectTimeout = 5000
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer)
    }
    // Start clock synchronization
    this.updateClock()
  }

  /**
   * Called when WebSocket encounters an error
   *
   * Attempts to reconnect only if connection is not already open.
   */
  private socketErrorHandler = (err: Event) => {
    console.log('*** AP Sync Client Error: ', err)
    // Only reconnect if connection is not open
    if (this.client != null && this.client.readyState !== WebSocket.OPEN) {
      this.reconnect()
    }
  }

  /**
   * Called when WebSocket connection closes
   *
   * Automatically attempts to reconnect unless we're already in the process.
   */
  private socketCloseHandler = () => {
    console.log('*** AP Sync Client: Client Closed')
    // Avoid double reconnection attempts
    if (!this.connecting) {
      this.reconnect()
    }
  }

  /**
   * Handle incoming WebSocket messages
   *
   * Routes messages to appropriate handlers based on message type:
   * - 'sync': Clock synchronization responses
   * - 'change': State change events (play/pause/track changes)
   * - 'state': Initial state snapshot on connection
   * - 'system': System status messages (server ready/unavailable)
   *
   * @param msg - Raw message event from WebSocket
   */
  private socketMessageHandler = (msg: MessageEvent) => {
    try {
      const parsedObject = JSON.parse(msg.data)
      switch (parsedObject.type) {
        case 'sync':
          // Clock synchronization response
          this.syncHandler(parsedObject)
          break
        case 'change':
          // Player state change (e.g., play, pause, track switch)
          this.changeHandler(parsedObject)
          break
        case 'state':
          // Initial state when connecting (complete snapshot)
          this.stateHandler(parsedObject)
          break
        case 'system':
          // Server status updates (available/unavailable)
          this.systemHandler(parsedObject)
          break
        default:
          console.log('*** AP Sync Client: Received unknown message.')
          break
      }
    } catch (e) {
      console.log('*** AP Sync Client: Failed to parse message JSON. \n', e)
    }
  }

  /**
   * Establish WebSocket connection
   *
   * Creates a new connection if one doesn't exist, or closes existing one first.
   * Sets up event handlers for connection lifecycle and message handling.
   */
  public connect() {
    // Close existing connection if present
    console.log('*** AP Sync Client: Client Closed Intentionally')
    if (this.client != null) {
      this.client.close()
      this.client = null
    }
    console.log('*** AP Sync Client: Connecting...')
    this.client = new WebSocket(this.connectionString)
    this.client.onopen = this.socketOpenHandler
    this.client.onerror = this.socketErrorHandler
    this.client.onclose = this.socketCloseHandler
    this.client.onmessage = this.socketMessageHandler
  }

  /**
   * Close WebSocket connection gracefully
   */
  public close() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.client != null) {
      // The user has intentionally disconnected, so don't attempt to
      // reconnect automatically.
      this.client.onclose = null
      this.client.onerror = null
      this.client.close()
    }
  }

  /**
   * Get current epoch time adjusted for server clock offset
   *
   * This provides the "server time" on the client, used to synchronize
   * playback timing across multiple devices.
   *
   * @returns Current time adjusted for clock offset (milliseconds since epoch)
   */
  getEpochTime() {
    return (
      Math.abs(performance.timeOrigin + performance.now()) + this.clockOffset
    )
  }

  /**
   * Send a message over WebSocket
   *
   * Only sends if connection is open. Silently logs errors if connection
   * is closed or send fails.
   *
   * @param msg - Message to send (should be JSON string)
   */
  send(msg: string) {
    try {
      // Debug log (commented out):
      // console.log("*** AP Sync Client: WS Client sending:", msg);
      if (this.client != null && this.client.readyState === WebSocket.OPEN) {
        this.client.send(msg)
      } else {
        console.log('*** AP Sync Client: Connection Closed - Cannot Send')
      }
    } catch (err) {
      console.log('*** AP Sync Client: Error sending: ', err)
    }
  }
}
